import { faFlag, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import 'video-react/dist/video-react.css';
import '../../assets/stylesheets/components/event-flag.scss';
import { useFlags } from '../../hooks/useFlags';
import { EventsService } from '../../services/events.service';
import { useHasPermission } from '../../hooks';

interface Props {
  id: number | null;
  event_id: number;
  onUpdate?: (event: any) => void;
}

export function EventFlag(props: Props) {
  const { t } = useTranslation('events_common');
  const flags = useFlags();
  const hasWritePermission = useHasPermission('events:write:flag');

  const [show, setShow] = useState(false);
  const ref = useRef(null);

  // a bit of song and dance to handle hiding the overlay without triggering click actions
  // as soon as we show the popover with flags to choose from, we hijack all the window clicks
  // - so that clicking anywhere but on the icon can dismiss the popover
  useEffect(() => {
    if (show) {
      window.addEventListener('click', handleClickOutside, true);
    } else {
      window.removeEventListener('click', handleClickOutside, true);
    }

    return () => window.removeEventListener('click', handleClickOutside, true);
  }, [show]);

  const handleClick = (event: any) => {
    setShow(true);
    event.stopPropagation();
  };

  const handleClickOutside = (e: any) => {
    // click target would be something like svg.path, but we can check if its any child of
    // a clickable element (.flag-clicker)
    if (e.target.matches('.flag-clicker, .flag-clicker *')) {
      // if that click was indeed on a flag, we find the closest clicker element
      // and it contains the flag information in a data-attribute;
      const closest = e.target.closest('.flag-clicker');
      const attr = closest.getAttribute('data-flag');
      const parsed = JSON.parse(attr);

      EventsService.updateEvent(parsed.event_id, { flag: parsed.flag_id }).then((event) => {
        if (props.onUpdate) {
          props.onUpdate(event);
        }
      });
    }

    // regardless of what clicked, hide the popover
    setShow(false);
    e.preventDefault();
    e.stopPropagation();
  };

  const flag = flags.find((f) => f.id === props.id);

  const color = flag?.color || 'white';

  return (
    <div className="hdn-event-flag" ref={ref}>
      <div
        className="hdn-event-flag__container"
        onClick={hasWritePermission ? handleClick : undefined}
        role="button"
        aria-label={t('flag.flag_event')}
        aria-expanded={show}
        aria-controls={show ? 'popover-contained' : undefined}
      >
        <FontAwesomeIcon
          style={{ color }}
          icon={faFlag}
          className={`fa-flag ${!flag && 'no-flag'}`}
          aria-label={flag ? t('flag.event_flag', { name: flag.name }) : t('flag.not_flagged')}
        />
      </div>

      <Overlay show={show} target={ref.current} placement="top" containerPadding={20} container={document.body}>
        <Popover id="popover-contained" className="hdn-event-flag__popover">
          <Popover.Content>
            <div
              className="hdn-event-flag__selector flag-clicker"
              data-flag={JSON.stringify({ flag_id: null, event_id: props.event_id })}
              style={{ color: 'white' }}
              tabIndex={0}
              role="button"
            >
              <FontAwesomeIcon className="fa-times" icon={faTimes} title={t('flag.remove_flag')} aria-label={t('flag.remove_flag')} />
            </div>

            {flags?.map((f) => (
              <div
                className="hdn-event-flag__selector flag-clicker"
                key={f.id}
                style={{ color: f.color }}
                data-test={`flag-${f.color}`}
                data-flag={JSON.stringify({ flag_id: f.id, event_id: props.event_id })}
                aria-label={f.name}
                tabIndex={0}
                role="button"
              >
                <FontAwesomeIcon key={f.id} className="fa-flag" icon={faFlag} aria-label={f.name} />
              </div>
            ))}
          </Popover.Content>
        </Popover>
      </Overlay>
    </div>
  );
}
